import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import useDriftClient from 'src/hooks/useDriftClient';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import { useCallback } from 'react';
import invariant from 'tiny-invariant';
import { BigNum, OrderType, PRICE_PRECISION } from '@velocity-exchange/sdk';
import {
	createEditOrderIx,
	CreateEditOrderIxParams,
	ENUM_UTILS,
	UIMarket,
	UISerializableOrder,
} from '@velocity-exchange/common';
import { notify } from 'src/utils/notifications';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import Env, {
	syncGetCurrentSettings,
} from 'src/environmentVariables/EnvironmentVariables';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { TRADE_PREP_UTILS } from 'src/stores/DriftStore/actions/actionHelpers/tradePrep';
import { ORDER_PREP_UTILS } from 'src/stores/DriftStore/actions/actionHelpers/orderPrep';
import { SlippageTolerance } from 'src/@types/types';
import { useTxErrorHandler } from 'src/hooks/driftActions/transactions/useTxErrorHandler';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useAutoSignDriftActionWithFallback } from 'src/hooks/wallets/useAutoSignDriftActionWithFallback';
import { FeePayerError } from 'src/constants/errors/FeePayerError';

type EditableNonMarketOrderParams = {
	baseAmount?: BigNum;
	limitPrice?: BigNum;
	triggerPrice?: BigNum;
	oraclePriceOffset?: number;
};

export const useEditNonMarketOrder = () => {
	const driftClient = useDriftClient();
	const isGeoBlocked = useDriftStore((s) => s.isGeoblocked);
	const currentUserAccount = useCurrentUserAccount();

	const getTxParams = useGetTxParams();
	const getOraclePrice = useGetOraclePriceForMarket();
	const handleTxError = useTxErrorHandler();

	const editNonMarketOrder = useAutoSignDriftActionWithFallback(
		useCallback(
			async (
				signerWallet: UiEnhancedIWalletV2,
				isAutoSigned: boolean,
				toastId: string,
				existingOrder: UISerializableOrder,
				slippageTolerance: SlippageTolerance,
				newOrderParams: EditableNonMarketOrderParams
			) => {
				if (isGeoBlocked) {
					notify({
						type: 'error',
						message: 'Geoblocked',
						description:
							'You are not allowed to use Drift from a restricted territory',
						lengthMs: 5000,
					});
					return false;
				}

				invariant(driftClient, 'driftClient is required');
				invariant(currentUserAccount?.client, 'user client is required');

				const isMarketOrder =
					ENUM_UTILS.match(existingOrder.orderType, OrderType.MARKET) ||
					ENUM_UTILS.match(existingOrder.orderType, OrderType.ORACLE);
				invariant(!isMarketOrder, 'Market orders cannot be edited');

				const uiMarket = new UIMarket(
					existingOrder.marketIndex,
					existingOrder.marketType
				);
				const isLimitOrder = ENUM_UTILS.match(
					existingOrder.orderType,
					OrderType.LIMIT
				);

				notify({
					id: toastId,
					type: isAutoSigned ? 'awaiting' : 'awaiting_wallet_confirmation',
					message: isAutoSigned ? 'Editing order...' : undefined,
					showUntilCancelled: true,
				});

				const editOrderOptions = {
					newBaseAmount: newOrderParams.baseAmount?.val,
					newLimitPrice: newOrderParams.limitPrice?.val,
					newOraclePriceOffset: newOrderParams.oraclePriceOffset
						? newOrderParams.oraclePriceOffset * PRICE_PRECISION.toNumber()
						: undefined,
					newTriggerPrice: newOrderParams.triggerPrice?.val,
				};

				const settings = syncGetCurrentSettings();
				const auctionStartPriceOffset =
					TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffset(
						settings?.auctionStartPriceOffset as number | 'marketBased',
						uiMarket.marketId
					);
				const oraclePrice = getOraclePrice(uiMarket.marketId);

				let limitAuctionOrderConfig:
					| CreateEditOrderIxParams['limitAuctionOrderConfig']
					| undefined = undefined;

				const isOracleLimitOrder =
					existingOrder.oraclePriceOffset &&
					!existingOrder.oraclePriceOffset.eqZero();
				if (isLimitOrder && !isOracleLimitOrder) {
					const marketAccount = uiMarket.isPerp
						? driftClient.getPerpMarketAccount(existingOrder.marketIndex)
						: driftClient.getSpotMarketAccount(existingOrder.marketIndex);
					invariant(marketAccount, 'marketAccount is required');
					invariant(newOrderParams.limitPrice, 'limitPrice is required');

					const optionalAuctionParamsInputs =
						ORDER_PREP_UTILS.getOptionalAuctionParamsInputs({
							slippageTolerance,
							marketAccount,
							overrideIsOracleOrder: {
								isOracleOrder: false, // we don't use oracle order parameters for limit auction orders
							},
							uiMarket,
							maxLeverageSelected: false,
						});
					limitAuctionOrderConfig = {
						orderType: 'limit',
						limitPrice: newOrderParams.limitPrice.val,
						limitAuction: {
							enable: true,
							dlobServerHttpUrl: Env.dlobServerHttpUrl,
							auctionStartPriceOffset,
							oraclePrice: oraclePrice.val,
							optionalLimitAuctionParams: optionalAuctionParamsInputs,
						},
					};
				}

				try {
					const editOrderIx = await createEditOrderIx({
						velocityClient: driftClient,
						user: currentUserAccount.client,
						orderId: existingOrder.orderId,
						editOrderParams: editOrderOptions,
						limitAuctionOrderConfig,
						mainSignerOverride: signerWallet.publicKey,
					});

					const txParams = getTxParams();
					await signerWallet.signAndSendTransaction(editOrderIx, txParams);

					notify({
						type: 'success',
						message: 'Order edited.',
						id: toastId,
						updatePrevious: true,
					});

					return true;
				} catch (error) {
					console.error('Error editing non-market order', error);

					if (error instanceof FeePayerError) {
						throw error;
					}

					handleTxError(error, toastId);

					return false;
				}
			},
			[
				isGeoBlocked,
				driftClient,
				currentUserAccount,
				getTxParams,
				getOraclePrice,
				handleTxError,
			]
		)
	);

	return { editNonMarketOrder };
};
